import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText, {
  Opts as ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import { View } from '@play-co/timestep-core/ui';
import { Avatar } from '../components/shared/Avatar';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { Feature } from 'src/replicant/analytics';

namespace style {
  export const button: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    labelOffsetY: -1,
    fontSize: 46,
    width: 240,
    height: 100,
    centerOnOrigin: true,
    font: 'Body',
  };

  export const separator = {
    height: 3,
    width: 500,
    y: 125,
    backgroundColor: '#e7b77e',
    centerOnOrigin: true,
  };

  export const message = {
    color: '#b68351',
    size: 40,
    align: 'center',
    centerOnOrigin: true,
    font: 'Body',
    width: 432,
    height: 48,
    y: 100,
  } as const;

  export const warning = {
    color: '#b68351',
    size: 22,
    align: 'center',
    verticalAlign: 'center',
    centerOnOrigin: true,
    font: 'Body',
    width: 300,
    height: 35,
    y: 355,
  } as const;

  export const frame = {
    height: 160,
    y: 240,
    image: 'assets/ui/shared/lossless/scroller_panel_blue.png',
    scaleMethod: '9slice',
    sourceSlices: {
      vertical: { top: 35, bottom: 35 },
      horizontal: { left: 35, right: 35 },
    },
    centerOnOrigin: true,
  } as const;

  export const playerName = {
    x: 145,
    width: 360,
    align: 'center',
    verticalAlign: 'center',
    font: 'Body',
    size: 35,
    color: '#947372',
  } as const;

  export const messageMulti = {
    ...message,
    y: 115,
    width: 490,
    height: 70,
  } as const;

  export const separatorMulti = {
    ...separator,
    y: 160,
  };

  export const frameMulti = {
    height: 200,
    y: 280,
    flex: {
      flexWrap: 'wrap',
      alignContent: 'center',
      justifyContent: 'center',
    },
    centerOnOrigin: true,
  } as const;

  export const warningMulti = {
    ...warning,
    width: 500,
    height: 75,
    y: 425,
    wordWrap: true,
  };

  export const rootMulti = {
    zIndex: 10000,
    width: uiConfig.popups.buruburu.width,
    height: 600,
    darkerBg: false,
  };
}

export default class PopupConfirmInviteMulti extends PopupBasic<
  (canceled: boolean) => void
> {
  private avatars: Avatar[];

  constructor(opts: { superview: View; close: (canceled: boolean) => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(true),
      ...style.rootMulti,
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: Feature.INVITE.INVITE_POPUP_MULTI,
      },
      titleIcon: {
        image: 'assets/ui/buruburu/friend_referral_icon.png',
      },
    });

    this.buttonClose.removeFromSuperview();

    // Top Separator
    new View({
      superview: this.box,
      ...style.separatorMulti,
      x: this.box.style.width / 2,
    });

    this.message.updateOpts({
      superview: this.box,
      ...style.messageMulti,
      x: this.box.style.width / 2,
    });

    const frame = new FlexBoxView({
      superview: this.box,
      ...style.frameMulti,
      width: this.box.style.width * 0.9,
      x: this.box.style.width / 2,
    });

    this.avatars = Array(10)
      .fill(null)
      .map(() => {
        const avatar = new Avatar({
          superview: frame,
          width: 85,
          height: 85,
          centerOnOrigin: false,
        });
        getFlexNode(avatar).marginHorizontal = 5;
        getFlexNode(avatar).marginVertical = 5;

        return avatar;
      });

    new LangBitmapFontTextView({
      superview: this.box,
      ...style.warningMulti,
      x: this.box.style.width / 2,
      localeText: () => i18n('social.invite.referral.noticeMulti'),
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      ...style.button,
      superview: this.box,
      x: this.box.style.width / 4,
      y: this.box.style.height - 25 - 100 / 2,
      localeText: () => i18n('basic.cancel'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: Feature.INVITE.INVITE_POPUP_MULTI_CANCEL,
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      ...style.button,
      x: (this.box.style.width / 4) * 3,
      y: this.box.style.height - 25 - 100 / 2,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: Feature.INVITE.INVITE_POPUP_MULTI_OK,
      },
    });
  }

  public init({ avatars }: { avatars: string[] }) {
    super.init({
      title: i18n('social.invite.referral.title'),
      message: i18n('social.invite.referral.confirmMulti', {
        numFriends: avatars.length,
      }),
    });

    this.avatars.forEach((avatar, i) => {
      if (!avatars[i]) {
        avatar.hide();
        return;
      }

      avatar.show();
      avatar.updateImage(avatars[i]);
    });
  }
}
